import React from 'react';
import { Typography, Box, Link } from '@mui/material';
import { postMemberPreLoanRequest } from '../../../queries/preloan';
import heroImg from '../../../assets/advance_offer_hero.jpg';
import bancameLogo from '../../../assets/bancame_logo_white.svg';
import HeroDialog from '../../../components/HeroDialog';

const SESSION_KEY = 'pu-advance-offer-dismissed';

type AdvanceOfferDialogProps = {
  token: string;
  origin?: string;
  onDismiss?: () => void;
  currentPlrStep?: string;
};

export default function AdvanceOfferDialog({
  token, origin, onDismiss, currentPlrStep,
}: AdvanceOfferDialogProps) {
  const dismissed = sessionStorage.getItem(SESSION_KEY) === 'true';
  const [open, setOpen] = React.useState(!dismissed);
  const [loading, setLoading] = React.useState(false);

  const handleDismiss = () => {
    sessionStorage.setItem(SESSION_KEY, 'true');
    setOpen(false);
    if (onDismiss) onDismiss();
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const res = await postMemberPreLoanRequest(token, origin);
      window.location.assign(res.data.url);
    } catch {
      setLoading(false);
      handleDismiss();
    }
  };

  if (!open) return null;

  const hasCurrentPlrStep = currentPlrStep && currentPlrStep !== 'NEEDS_CU';

  return (
    <HeroDialog
      open={open}
      onClose={handleDismiss}
      heroImage={heroImg}
      loading={loading}
      onConfirm={handleConfirm}
      buttonText={hasCurrentPlrStep ? 'Continuar solicitud de avance' : 'Solicitar avance'}
      pageCall={{ category: 'PU - TU CREDITO', name: 'DIALOGO OFERTA AVANCE' }}
      logo={(
        <Box
          component="img"
          src={bancameLogo}
          alt="banca.me"
          sx={{
            height: 18, width: 'auto', display: 'block', mb: '10px',
          }}
        />
      )}
      title={(
        <Typography
          variant="h3"
          color="primary"
          sx={{
            fontWeight: 800,
            letterSpacing: '-1.4px',
            lineHeight: 1.05,
            mb: '14px',
            fontFamily: 'Inter, Helvetica, Arial, sans-serif',
          }}
        >
          Tu crédito, a tu medida
        </Typography>
      )}
      body={(
        <Typography variant="body1" sx={{ fontSize: '15.5px', lineHeight: 1.55, mb: '24px' }}>
          {hasCurrentPlrStep ? (
            <Typography component="span" variant="body1">
              Continúa con tu
              {' '}
              <b>solicitud de avance</b>
              {' '}
              con
              {' '}
              <Typography component="span" variant="body1" color="secondary" fontWeight="bold">
                banca.me
              </Typography>
            </Typography>
          )
            : (
              <span>
                {' '}
                Solicita tu avance con
                {' '}
                <Typography component="span" variant="body1" color="secondary" fontWeight="bold">
                  banca.me
                </Typography>
                .
                {' '}
                Evalúate en 2 minutos, elige tu monto y tus cuotas,
              </span>
            )}
          {' '}
          <span>y en menos de 24 horas tendrás el dinero en tu cuenta.</span>
        </Typography>
      )}
      footer={(
        hasCurrentPlrStep ? <Box marginTop={2} /> : (
          <Typography
            sx={{
              fontSize: '11.5px',
              color: '#6e6e73',
              lineHeight: 1.5,
              mt: '20px',
              pt: '14px',
              borderTop: '1px solid #E7E2F0',
            }}
          >
            Al hacer click en &quot;Solicitar avance&quot; aceptas los
            {' '}
            <Link href="/terms-and-conditions" target="_blank" rel="noopener">
              términos y condiciones
            </Link>
            {' '}
            y
            {' '}
            <Link href="/privacy-policy" target="_blank" rel="noopener">
              política de privacidad
            </Link>
            {' '}
            lo que incluye la consulta de cuentas y deudas asociadas a mi RUT.
          </Typography>
        )
      )}
    />
  );
}

AdvanceOfferDialog.defaultProps = {
  origin: 'portal-usuario',
  onDismiss: undefined,
  currentPlrStep: null,
};
